import { useEffect, useState } from 'react'

/**
 * Avisa cuando un elemento entra en pantalla, una sola vez.
 *
 * Es la pieza que comparten las animaciones que arrancan al hacer scroll: el
 * revelado de las tarjetas (A-06) y la cascada de las filas de tabla. Se separo
 * del hook de revelado porque la cascada no necesita las clases de aquel, solo
 * saber si ya se ve.
 *
 * La ref es de callback, no un `useRef`: varios de estos bloques se pintan
 * primero en estado de carga y el nodo real llega despues. Con `useRef` el
 * efecto ya habia corrido contra un nodo que no existia y el contenido se
 * quedaba invisible para siempre.
 */
export function useEnPantalla<T extends HTMLElement = HTMLDivElement>(
  umbral = 0.15,
) {
  const [nodo, setNodo] = useState<T | null>(null)
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    if (!nodo || visible) return

    // Sin IntersectionObserver (jsdom en las pruebas, navegadores viejos) se
    // muestra de una vez: vale más ver el contenido que animarlo.
    if (typeof IntersectionObserver === 'undefined') {
      setVisible(true)
      return
    }

    const observador = new IntersectionObserver(
      (entradas) => {
        for (const entrada of entradas) {
          if (!entrada.isIntersecting) continue
          setVisible(true)
          observador.unobserve(entrada.target)
        }
      },
      { threshold: umbral },
    )

    observador.observe(nodo)

    // En una pestaña en segundo plano el observador no dispara, y el contenido
    // se quedaria invisible. Al volver a la pestaña se vuelve a observar, que
    // fuerza una lectura nueva.
    const alVolver = () => {
      if (document.hidden) return
      observador.unobserve(nodo)
      observador.observe(nodo)
    }
    document.addEventListener('visibilitychange', alVolver)

    return () => {
      document.removeEventListener('visibilitychange', alVolver)
      observador.disconnect()
    }
  }, [nodo, visible, umbral])

  return { ref: setNodo, visible }
}
